import "@xyflow/react/dist/style.css";
import "./styles/visualQueryBuilder.css";

import { Button, Empty, Input, Spin, Tooltip, notification } from "antd";
import { AgGridReact } from "ag-grid-react";
import {
  addEdge,
  Background,
  BaseEdge,
  Controls,
  EdgeLabelRenderer,
  getBezierPath,
  Handle,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from "@xyflow/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Database, Play, Plus, Search, Table, Trash2, X } from "react-feather";
import { VisualQueryService } from "./services/visualQueryService";

const quote = (value) => `[${String(value)}]`;

const VisualQueryNode = ({ data }) => (
  <div className="visual-query-node">
    <div className="visual-query-node-title">
      <Table size={15} />
      <span>{data.schema}.{data.name}</span>
      <button
        aria-label={`Remove ${data.name}`}
        className="visual-query-node-delete nodrag"
        onClick={() => data.removeView()}
        title="Remove data view"
        type="button"
      >
        <X size={14} />
      </button>
    </div>
    <div className="visual-query-column-list">
      {data.columns.map((column) => (
        <div className="visual-query-column" key={column.name}>
          <Handle id={column.name} type="target" position={Position.Left} />
          <label>
            <input
              checked={data.selectedColumns.includes(column.name)}
              onChange={() => data.toggleColumn(column.name)}
              type="checkbox"
            />
            <span>{column.name}</span>
          </label>
          <small>{column.dataType}</small>
          <Handle id={column.name} type="source" position={Position.Right} />
        </div>
      ))}
    </div>
  </div>
);

const JoinEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data }) => {
  const [edgePath, labelX, labelY] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
  return (
    <>
      <BaseEdge id={id} path={edgePath} />
      <EdgeLabelRenderer>
        <div
          className="visual-query-join-label nodrag nopan"
          style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}
        >
          <span>{data.label}</span>
          <button aria-label="Remove join" onClick={() => data.removeJoin(id)} title="Remove join" type="button">
            <Trash2 size={11} />
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

const nodeTypes = { visualQueryView: VisualQueryNode };
const edgeTypes = { visualQueryJoin: JoinEdge };

export const VisualQueryBuilder = () => {
  const [views, setViews] = useState([]);
  const [viewSearch, setViewSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [result, setResult] = useState({ columns: [], rows: [], sql: "" });

  useEffect(() => {
    VisualQueryService.getViews()
      .then(setViews)
      .catch(() => notification.error({ message: "Query Builder", description: "Unable to load database views." }))
      .finally(() => setLoading(false));
  }, []);

  const toggleColumn = useCallback((nodeId, column) => {
    setNodes((currentNodes) => currentNodes.map((node) => {
      if (node.id !== nodeId) return node;
      const selectedColumns = node.data.selectedColumns.includes(column)
        ? node.data.selectedColumns.filter((name) => name !== column)
        : [...node.data.selectedColumns, column];
      return { ...node, data: { ...node.data, selectedColumns } };
    }));
  }, [setNodes]);

  const removeView = useCallback((nodeId) => {
    setNodes((currentNodes) => currentNodes.filter((node) => node.id !== nodeId));
    setEdges((currentEdges) => currentEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
  }, [setEdges, setNodes]);

  const removeJoin = useCallback((edgeId) => {
    setEdges((currentEdges) => currentEdges.filter((edge) => edge.id !== edgeId));
  }, [setEdges]);

  const addView = useCallback((view) => {
    setNodes((currentNodes) => {
      if (currentNodes.some((node) => node.data.schema === view.schema && node.data.name === view.name)) return currentNodes;
      const id = `view-${view.schema}-${view.name}`;
      const offset = currentNodes.length * 36;
      return [...currentNodes, {
        id,
        type: "visualQueryView",
        position: { x: 80 + offset, y: 70 + offset },
        data: {
          ...view,
          selectedColumns: [],
          toggleColumn: (column) => toggleColumn(id, column),
          removeView: () => removeView(id),
        },
      }];
    });
  }, [removeView, setNodes, toggleColumn]);

  const onConnect = useCallback((connection) => {
    if (!connection.sourceHandle || !connection.targetHandle || connection.source === connection.target) return;
    const id = `${connection.source}-${connection.sourceHandle}-${connection.target}-${connection.targetHandle}`;
    setEdges((currentEdges) => currentEdges.some((edge) => edge.id === id) ? currentEdges : addEdge({
      ...connection,
      id,
      data: { label: `${connection.sourceHandle} = ${connection.targetHandle}`, removeJoin },
      type: "visualQueryJoin",
    }, currentEdges));
  }, [removeJoin, setEdges]);

  const filteredViews = useMemo(() => {
    const search = viewSearch.trim().toLowerCase();
    return search ? views.filter((view) => `${view.schema}.${view.name}`.toLowerCase().includes(search)) : views;
  }, [viewSearch, views]);

  const query = useMemo(() => ({
    views: nodes.map((node) => ({ id: node.id, schema: node.data.schema, name: node.data.name, columns: node.data.selectedColumns })),
    joins: edges.map((edge) => ({ sourceViewId: edge.source, sourceColumn: edge.sourceHandle, targetViewId: edge.target, targetColumn: edge.targetHandle })),
  }), [nodes, edges]);

  const sqlPreview = useMemo(() => {
    if (!query.views.length) return "-- Add a data view to begin";
    const viewLookup = new Map(query.views.map((view, index) => [view.id, { ...view, alias: `v${index + 1}` }]));
    const columns = query.views.flatMap((view) => view.columns.map((column) => `${viewLookup.get(view.id).alias}.${quote(column)} AS ${quote(`${view.name}_${column}`)}`));
    if (!columns.length) return "-- Select one or more columns";
    const root = viewLookup.get(query.views[0].id);
    const joins = query.joins.map((join) => {
      const source = viewLookup.get(join.sourceViewId);
      const target = viewLookup.get(join.targetViewId);
      return source && target ? `INNER JOIN ${quote(target.schema)}.${quote(target.name)} ${target.alias} ON ${source.alias}.${quote(join.sourceColumn)} = ${target.alias}.${quote(join.targetColumn)}` : "";
    }).filter(Boolean);
    return `SELECT TOP (1000)\n  ${columns.join(",\n  ")}\nFROM ${quote(root.schema)}.${quote(root.name)} ${root.alias}${joins.length ? `\n${joins.join("\n")}` : ""};`;
  }, [query]);

  const runQuery = async () => {
    try {
      setRunning(true);
      const response = await VisualQueryService.run(query);
      setResult(response);
    } catch (error) {
      notification.error({ message: "Query Builder", description: error.response?.body?.message || "Unable to run visual query." });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="visual-query-builder">
      <aside className="visual-query-palette">
        <div className="visual-query-section-title"><Database size={16} /> Data Views</div>
        <Input
          className="visual-query-search"
          onChange={(event) => setViewSearch(event.target.value)}
          placeholder="Search data views"
          prefix={<Search size={14} />}
          value={viewSearch}
        />
        {loading ? <Spin /> : filteredViews.map((view) => (
          <button className="visual-query-view-button" key={`${view.schema}.${view.name}`} onClick={() => addView(view)} type="button">
            <Plus size={14} /><span>{view.schema}.{view.name}</span>
          </button>
        ))}
        {!loading && !filteredViews.length && <Empty description="No matching views" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
      </aside>
      <main className="visual-query-workspace">
        <div className="visual-query-canvas">
          <ReactFlow
            edges={edges}
            edgeTypes={edgeTypes}
            fitView
            nodeTypes={nodeTypes}
            nodes={nodes}
            onConnect={onConnect}
            onEdgesChange={onEdgesChange}
            onNodesChange={onNodesChange}
            onEdgesDelete={(deletedEdges) => setEdges((currentEdges) => currentEdges.filter((edge) => !deletedEdges.some((deleted) => deleted.id === edge.id)))}
            onNodesDelete={(deletedNodes) => {
              const deletedIds = new Set(deletedNodes.map((node) => node.id));
              setNodes((currentNodes) => currentNodes.filter((node) => !deletedIds.has(node.id)));
              setEdges((currentEdges) => currentEdges.filter((edge) => !deletedIds.has(edge.source) && !deletedIds.has(edge.target)));
            }}
          >
            <Background gap={18} size={1} />
            <Controls />
          </ReactFlow>
        </div>
        <section className="visual-query-sql">
          <div className="visual-query-section-title">Generated SQL</div>
          <pre>{result.sql || sqlPreview}</pre>
          <Tooltip title="Execute the generated, server-validated query">
            <Button disabled={!query.views.length || !query.views.some((view) => view.columns.length)} icon={<Play size={15} />} loading={running} onClick={runQuery} type="primary">Run Query</Button>
          </Tooltip>
        </section>
        <section className="visual-query-results">
          <div className="visual-query-section-title">Results</div>
          {result.rows.length ? (
            <div className="ag-theme-balham visual-query-result-grid">
              <AgGridReact columnDefs={result.columns} rowData={result.rows} />
            </div>
          ) : <Empty description="Run a visual query to see results" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
        </section>
      </main>
    </div>
  );
};